'use client';

import Link from 'next/link';
// Using native <img> instead of next/image for tenant logos to avoid hydration issues
import { usePathname, useRouter } from 'next/navigation';
import { TenantLogo } from '@/components/ui/tenant-logo';
import {
  LayoutDashboard,
  Menu,
  LogIn,
  LogOut,
  User,
  ExternalLink,
  Globe,
  Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useTenant } from '@/lib/tenant-context';
import { NotificationDropdown } from '@/components/notifications/notification-dropdown';
import { consumerHeaderNav } from '@/lib/consumer-navigation';
import { shouldHideAppChrome } from '@/lib/app-chrome-visibility';

export function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, isAuthenticated, logout, isLoading: authLoading } = useAuth();
  const { tenant, isTenantDomain, isLoading: tenantLoading } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [hasTenantCookie, setHasTenantCookie] = useState(false);
  const hydratedTenantDomain = mounted && isTenantDomain;

  useEffect(() => {
    setMounted(true);
    // Check if tenant cookie exists (indicates we're on a tenant subdomain)
    const tenantCookie = document.cookie.split(';').find(c => c.trim().startsWith('vhire_tenant='));
    setHasTenantCookie(!!tenantCookie);
  }, []);

  // Standalone shells and live sessions render bare. The list is shared with
  // the recruiter header (lib/app-chrome-visibility.ts) so the two cannot
  // disagree about which surfaces must stay free of navigation — see that
  // module for why this is a correctness rule and not a layout preference.
  //
  // `/admin` stays here rather than in the shared predicate: the two headers
  // genuinely disagree about it (the admin console renders its OWN header),
  // and that split belongs at the call site where it is legible.
  //
  // Defensive: usePathname() can be null during the very first SSR pass, in
  // which case we still want to skip rendering on these routes.
  const path = pathname ?? '';
  if (path.startsWith('/admin') || shouldHideAppChrome(path)) {
    return null;
  }

  // Get user initials
  const userInitials = user?.fullName
    ? user.fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() || '??';

  const handleLogout = () => {
    logout();
    router.push('/');
  };


  // Only show Dashboard link when authenticated (use mounted to avoid hydration mismatch)
  // Add company website link for tenant domains
  // First-party VHire candidate information is apex-only (the middleware gate
  // keeps it off tenant hosts), so surface it only on the main domain.
  const navItems = consumerHeaderNav({
    isTenantDomain: hydratedTenantDomain,
    isAuthenticated: mounted && isAuthenticated,
  });

  // Company website link for tenant domains - ensure it has https:// prefix
  const rawWebUrl = hydratedTenantDomain && tenant?.company_web_url ? tenant.company_web_url : null;
  const companyWebUrl = rawWebUrl 
    ? (rawWebUrl.startsWith('http://') || rawWebUrl.startsWith('https://') ? rawWebUrl : `https://${rawWebUrl}`)
    : null;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo - Show tenant branding if on tenant domain (only after mounted to avoid hydration mismatch) */}
        {/*
          prefetch={false} — the home page is heavy and we don't want every
          page load to prefetch it. Users who click the logo are intentionally
          navigating away; the small perceived delay is acceptable.
          Phase 1.4, perf benchmark 2026-05-21.
        */}
        <Link href="/" prefetch={false} className="flex items-center gap-2">
          {mounted && !tenantLoading && isTenantDomain && tenant?.branding?.logo_url ? (
            // Tenant logo. width/height are a nominal bounding box (max-w-[160px] + h-9);
            // object-contain handles the real aspect ratio. The system dark-mode swap
            // and resize live in <TenantLogo>; hideOnError mirrors the prior native-img
            // fallback (hide if the tenant's uploaded URL 404s).
            <TenantLogo
              light={tenant.branding.logo_url}
              dark={tenant.branding.logo_dark_url}
              alt={tenant.company_name || tenant.name}
              width={160}
              height={36}
              className="h-9 w-auto object-contain max-w-[160px]"
              hideOnError
            />
          ) : tenantLoading && hasTenantCookie ? (
            // Skeleton placeholder while loading tenant branding (prevents FOIC)
            <div className="h-9 w-32 bg-muted animate-pulse rounded-lg" />
          ) : (
            // Default VHire logo (shown on main domain only).
            // Intrinsic SVG viewBox is 260x80 (3.25:1). At h-9 (36px), w-auto = 117px.
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/logo.svg" alt="VHire" width={117} height={36} className="h-9 w-auto dark:hidden" />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/logo-white.svg" alt="VHire" width={117} height={36} className="h-9 w-auto hidden dark:block" />
            </>
          )}
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href;
            return (
              <Button
                key={item.href}
                asChild
                variant={isActive ? 'secondary' : 'ghost'}
                size="sm"
                className="gap-2"
              >
                <Link href={item.href} prefetch={false} aria-current={isActive ? 'page' : undefined}>
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              </Button>
            );
          })}
          {/* Company website link for tenant domains - optional external link */}
          {companyWebUrl && (
            <Button asChild variant="ghost" size="sm" className="gap-2">
              <a href={companyWebUrl} target="_blank" rel="noopener noreferrer">
                <Globe className="w-4 h-4" />
                Website
                <ExternalLink className="w-3 h-3 opacity-50" />
              </a>
            </Button>
          )}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
{/* Only render auth-dependent UI after mounting to avoid hydration mismatch */}
          {mounted && isAuthenticated && (
            <NotificationDropdown token={typeof window !== 'undefined' ? localStorage.getItem('vhire_token') : null} />
          )}

          {mounted && isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" aria-label="Account menu">
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-primary-action text-primary-foreground text-sm">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link href="/me/profile" prefetch={false} className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    My Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/me/dashboard" prefetch={false} className="flex items-center gap-2">
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-destructive">
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
) : mounted && !isTenantDomain ? (
            <div className="flex items-center gap-2">
              {/* Keep both audiences' shipped entry points discoverable on the
                  apex. The employer page has its own reviewed marketing shell;
                  tenant domains remain candidate-only. */}
              <Button asChild variant="outline" size="sm" className="hidden gap-1.5 sm:inline-flex">
                <Link href="/employer" prefetch={false}>
                  <Building2 className="w-4 h-4" />
                  For Employers
                </Link>
              </Button>
              {authLoading ? (
                <Button variant="default" size="sm" className="gap-2" disabled>
                  <LogIn className="w-4 h-4" />
                  Candidate Login
                </Button>
              ) : (
                <Button asChild variant="default" size="sm" className="gap-2">
                  <Link href="/auth/login" prefetch={false}>
                    <LogIn className="w-4 h-4" />
                    Candidate Login
                  </Link>
                </Button>
              )}
            </div>
          ) : null}

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64">
              <SheetTitle className="sr-only">Site navigation</SheetTitle>
              <div className="flex flex-col gap-4 mt-8">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      prefetch={false}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </Link>
                  );
                })}

                {/* Company website link for mobile */}
                {companyWebUrl && (
                  <a
                    href={companyWebUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"
                  >
                    <Globe className="w-5 h-5" />
                    Visit Main Site
                    <ExternalLink className="w-4 h-4 opacity-50 ml-auto" />
                  </a>
                )}

                <div className="border-t pt-4 mt-4">
                  {mounted && isAuthenticated ? (
                    <>
                      <Link
                        href="/me/profile"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted"
                      >
                        <User className="w-5 h-5" />
                        Profile
                      </Link>
                      <button
                        onClick={() => { handleLogout(); setIsOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted text-destructive"
                      >
                        <LogOut className="w-5 h-5" />
                        Sign Out
                      </button>
                    </>
                  ) : !hydratedTenantDomain ? (
                    <>
                      <Link
                        href="/employer"
                        prefetch={false}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg bg-primary/10 text-primary font-medium"
                      >
                        <Building2 className="w-5 h-5" />
                        For Employers
                      </Link>
                      <Link
                        href="/auth/login"
                        prefetch={false}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg bg-primary-action text-primary-foreground"
                      >
                        <LogIn className="w-5 h-5" />
                        Candidate Login
                      </Link>
                    </>
                  ) : null}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
